// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers. The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { ButtonIcon } from "@insite/mobius/Button/Props";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import get from "@insite/mobius/utilities/get";
import buttonSizeVariants from "@vanmeter/MobiusOverrides/Button/buttonSizeVariants";
import { fontConfig } from "@vanmeter/XDL/constants";
import { xBaseTheme } from "@vanmeter/XDL/types";
import { getSystemColor } from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

// Button color configurations
export const buttonColors: { [key: string]: { [key: string]: string } } = {
    primary: {
        main: "primary",
        hover: "common.ui15",
        focus: "common.ui16",
    },
    secondary: {
        main: "secondary",
        hover: "common.ui17",
        focus: "common.ui18",
    },
    tertiary: {
        main: "common.ui8",
        hover: "common.ui19",
        focus: "common.ui20",
    },
    link: {
        main: "text.link",
        hover: "common.ui15",
        focus: "common.ui16",
    },
};
const getSizeRelatedStyles = (size: keyof typeof buttonSizeVariants) => {
    return css`
        min-height: ${get(buttonSizeVariants, [size, "height"])}px;
        line-height: ${get(buttonSizeVariants, [size, "lineHeight"])};
        padding: ${get(buttonSizeVariants, [size, "padding"])};
    `;
};

const sharedButtonStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 30px;
    ${({ _sizeVariant }: any) => getSizeRelatedStyles(_sizeVariant)}

    ${ButtonIcon} {
        top: 0;
    }

    &.small {
        ${getSizeRelatedStyles("small")}
    }
    &.medium {
        ${getSizeRelatedStyles("medium")}
    }
    &.large {
        ${getSizeRelatedStyles("large")}
    }
    ${TypographyStyle} {
        font-weight: ${fontConfig.weight.bold};
    }
`;

export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.primary.main,
        buttonType: "solid",
        shape: "pill",
        mergeCss: true,
        hoverStyle: {
            color: `${getSystemColor(buttonColors.primary.hover)}`,
        },
        activeStyle: {
            color: `${getSystemColor(buttonColors.primary.focus)}`,
        },
        typographyProps: {
            fontFamily: fontConfig.secondaryFontFamily,
        },
        css: css`
            ${sharedButtonStyles}
        `,
    },
    secondary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.secondary.main,
        buttonType: "solid",
        shape: "pill",
        mergeCss: true,
        hoverStyle: {
            color: `${getSystemColor(buttonColors.secondary.hover)}`,
        },
        activeStyle: {
            color: `${getSystemColor(buttonColors.secondary.focus)}`,
        },
        typographyProps: {
            fontFamily: fontConfig.fontName[1],
        },
        css: css`
            ${sharedButtonStyles}
        `,
    },
    tertiary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.tertiary.main,
        buttonType: "solid",
        shape: "pill",
        mergeCss: true,
        hoverStyle: {
            color: `${getSystemColor(buttonColors.tertiary.hover)}`,
        },
        activeStyle: {
            color: `${getSystemColor(buttonColors.tertiary.focus)}`,
        },
        typographyProps: {
            fontFamily: fontConfig.fontName[1],
        },
        css: css`
            ${sharedButtonStyles}
        `,
    },
    link: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.link.main,
        buttonType: "outline",
        mergeCss: true,
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.link.hover)}`,
            color: `${getSystemColor(buttonColors.link.hover)}`,
        },
        activeStyle: {
            color: `${getSystemColor(buttonColors.link.focus)}`,
        },
        css: css`
            ${buttonTransparent}
            padding: 0;
        `,
    },
} as xBaseTheme["button"];
